import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import { apiCore } from '@/api/apiCore';
import { inviteAPI, type InviteParticipant } from '@/api/inviteAPI';
import { getConferenceManager } from '@/conference/conferenceManagerRef';
import { CONFERENCE_COMMANDS } from '@/conference/commands';
import SNSnackbar from '@luna-sena/sena-snackbar';
import apiManager from '@/api';

import type { InviteTarget } from './types';
import SelectedUserBox from './components/SelectedUserBox';
import OrganizationTab from './tabs/OrganizationTab';
import ContactTab from './tabs/ContactTab';
import ExternalTab from './tabs/ExternalTab';

type InviteTab = 'organization' | 'contact' | 'external';

const InviteUser: React.FC = () => {
  const dispatch = useAppDispatch();
  const local = useAppSelector((s) => s.local);
  const participants = useAppSelector((s) => s.participants);
  const invitedParticipantVersion = useAppSelector((s) => s.ui.invitedParticipantVersion);

  const [activeTab, setActiveTab] = useState<InviteTab>('organization');
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState({ text: '', version: 0 });
  const [selectedUsers, setSelectedUsers] = useState<InviteTarget[]>([]);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; type: 'info' | 'success' | 'error' | ''; message: string }>({
    open: false, type: '', message: '',
  });
  const meetingInfo = useMemo(() => {
    return {
      title: local.roomName || '진행중인 회의',
      id: local.roomId || '',
      companyNo: local.companyNo,
    };
  }, [local]);

  const cno = apiCore.hSelectedCno;
  const companyNo = meetingInfo.companyNo || cno;

  const handleCopyLink = useCallback(() => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      // TODO: 토스트
    });
  }, []);

  const handleBack = () => {
    dispatch(uiActions.setSidebarContent('participants'));
  };

  const { data: invitedParticipants } = useQuery({
    queryKey: ['invitedParticipants', meetingInfo.id, invitedParticipantVersion],
    queryFn: async () => {
      if (!meetingInfo.id) return [];
      try {
        const res = await apiManager.getInvitedParticipantList(meetingInfo.id);
        return res.participantList || [];
      } catch (err) {
        console.error('Failed to fetch invited participants', err);
        return [];
      }
    },
    enabled: !!meetingInfo.id,
    staleTime: Infinity, // version 기반 무효화이므로 수동 관리
  });

  const connectedWehagoIds = useMemo(() => {
    const ids = new Set<string>();
    if (local.wehagoId) ids.add(local.wehagoId);
    participants.participantIds.forEach(id => {
      const p = participants.participants[id];
      if (p?.wehagoId) ids.add(p.wehagoId);
    });
    if (invitedParticipants) {
      invitedParticipants.forEach(p => {
        if (p.portalId) ids.add(p.portalId);
      });
    }
    return ids;
  }, [local.wehagoId, participants, invitedParticipants]);

  const toggleUser = useCallback((user: InviteTarget) => {
    // 이미 회의에 참석 중인 사용자 체크
    if (user.type === 'USER' && user.portalId && connectedWehagoIds.has(user.portalId)) {
      setSnackbar({ open: true, type: 'info', message: '이미 참석한 사용자입니다.' });
      return;
    }
    setSelectedUsers(prev => {
      const exists = prev.find(p => p.id === user.id);
      if (exists) {
        return prev.filter(p => p.id !== user.id);
      } else if (user.type === 'ORGANIZATION' && user.members) {
        // 조직 선택 시 해당 조직 멤버의 개별 USER 항목이 이미 있으면 제거
        const memberIds = new Set(user.members.map(m => `USER_${m.userNo}_${m.companyNo}`));
        const filtered = prev.filter(p => !memberIds.has(p.id));
        return [...filtered, user];
      } else {
        return [...prev, user];
      }
    });
  }, [connectedWehagoIds]);

  const removeUser = useCallback((id: string) => {
    setSelectedUsers(prev => prev.filter(p => p.id !== id));
  }, []);

  // 조직 선택 상태에서 개별 멤버를 해제할 때: 조직을 분해하고 나머지 멤버를 개별 추가
  const removeUserFromOrg = useCallback((orgId: string, memberToExclude: InviteTarget, allOrgMembers: InviteTarget[]) => {
    setSelectedUsers(prev => {
      const withoutOrg = prev.filter(p => p.id !== orgId);
      const remainingMembers = allOrgMembers.filter(m => m.id !== memberToExclude.id);
      return [...withoutOrg, ...remainingMembers];
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (selectedUsers.length === 0 || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const participantList: InviteParticipant[] = [];

      selectedUsers.forEach(user => {
        if (user.type === 'ORGANIZATION' && user.members) {
          // 부서 선택 시 저장된 전 직원 정보 추가
          user.members.forEach(m => participantList.push({
            type: 'USER',
            companyNo: m.companyNo,
            userNo: m.userNo,
            portalId: m.portalId,
            userName: m.userName || null,
            profileUrl: m.profileUrl || null,
          }));
        } else if (user.type === 'USER' && user.userNo && user.companyNo && user.portalId) {
          participantList.push({
            type: 'USER',
            companyNo: user.companyNo,
            userNo: user.userNo,
            portalId: user.portalId,
            userName: user.name || null,
            profileUrl: user.profileUrl || null,
          });
        } else if (user.type === 'CONTACT' && user.phone) {
          participantList.push({
            type: 'PHONE',
            phone: user.phone.replace(/-/g, ''),
            userName: user.name || null,
            profileUrl: user.profileUrl || null,
          });
        } else if (user.type === 'EMAIL' && user.email) {
          participantList.push({
            type: 'EMAIL',
            email: user.email,
            userName: user.name && user.name !== user.email ? user.name : null,
            profileUrl: user.profileUrl || null,
          });
        }
      });

      // 중복 제거 (동일 portalId / phone / email)
      const seen = new Set<string>();
      const dedupedList = participantList.filter(p => {
        const key =
          p.type === 'USER' ? `USER:${p.portalId}` :
          p.type === 'PHONE' ? `PHONE:${p.phone}` :
          `EMAIL:${p.email}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      // 이미 회의에 참석 중인 사용자 필터링
      const alreadyJoinedCount = dedupedList.filter(p => p.type === 'USER' && p.portalId && connectedWehagoIds.has(p.portalId)).length;
      const filteredList = dedupedList.filter(p => {
        if (p.type === 'USER' && p.portalId && connectedWehagoIds.has(p.portalId)) return false;
        return true;
      });

      if (filteredList.length === 0) {
        setSnackbar({ open: true, type: 'info', message: alreadyJoinedCount > 0 ? '이미 회의에 참석하였습니다.' : '초대 대상자가 없습니다.' });
        return;
      }

      await inviteAPI.postInvite(String(meetingInfo.id), filteredList);

      // 초대 성공 → 초대된 참석자 목록 재조회 트리거 + 다른 참여자에게 브로드캐스트
      dispatch(uiActions.bumpInvitedParticipantVersion());
      try {
        const manager = getConferenceManager();
        manager?.sendCommandOnce(CONFERENCE_COMMANDS.INVITE_LIST_UPDATED, {
          value: String(Date.now()),
        });
      } catch (broadcastErr) {
        console.warn('[InviteUser] INVITE_LIST_UPDATED 브로드캐스트 실패:', broadcastErr);
      }

      setSnackbar({ open: true, type: 'success', message: '참석 알림을 전송하였습니다.' });
      dispatch(uiActions.setSidebarContent('participants'));
    } catch (error: any) {
      const errorCode = error?.response?.data?.resultData?.errorCode;
      const errorMessages: Record<string, { type: 'info' | 'success' | 'error'; message: string }> = {
        'IVE001': { type: 'error', message: '이미 회의에 참석하였습니다.' },
        'IVE002': { type: 'error', message: '이미 초대된 사용자입니다.' },
        'RME001': { type: 'error', message: '종료된 회의에는 초대할 수 없습니다.' },
        'RME002': { type: 'error', message: '최대 참석자 수를 초과하였습니다.' },
        'RQE004': { type: 'error', message: '초대 권한이 없습니다.' },
        'RQE001': { type: 'error', message: '회의방을 찾을 수 없습니다.' },
      };
      const matched = errorCode ? errorMessages[errorCode] : null;
      setSnackbar(matched
        ? { open: true, ...matched }
        : { open: true, type: 'error', message: '초대 중 오류가 발생했습니다.' },
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedUsers, meetingInfo.id, isSubmitting, dispatch, connectedWehagoIds]);

  const handleSearch = useCallback(() => {
    setSearchQuery(prev => {
      if (prev.text === inputValue) {
        return { ...prev, version: prev.version + 1 };
      }
      return { text: inputValue, version: 0 };
    });
  }, [inputValue]);

  const handleCancelSearch = useCallback(() => {
    setInputValue('');
    setSearchQuery({ text: '', version: 0 });
  }, []);

  return (
    <div className="new_chat typeOrgList">
      <div className="chat_tabs">
        <div className="chat_selection inFlex">
          <button type="button" className="btnPrev" onClick={handleBack}>
            <span className="blind">이전</span>
          </button>
          <h2>초대하기</h2>
          <div className="meetingInfo">
             <button type="button" className="ico_info" onClick={() => setIsTooltipOpen(!isTooltipOpen)}>
                <span className="blind">회의정보</span>
             </button>
             {isTooltipOpen && (
               <div className="viewerHeader__layer dropdown_basic">
                 <div className="shareInfo">
                   <dl className="shareInfoText">
                     <dt>회의제목</dt><dd>{meetingInfo.title}</dd>
                     <dt>회의ID</dt><dd>{meetingInfo.id}</dd>
                   </dl>
                 </div>
               </div>
             )}
          </div>
        </div>
        <div className="right_btns">
          <button
            type="button"
            className="chat_closeBtn"
            onClick={() => dispatch(uiActions.setOpenSideBar(false))}
          >
            <span className="blind">닫기</span>
          </button>
        </div>
      </div>

      <div className="rtcNew_joinMemberList_searchBox">
        <div className="common_round_searchbox4 notosans_font typeBlack">
          <div className={`common_round_searchbox4__flexWrap ${inputValue ? 'showCancel' : ''}`}>
            <input
              type="text"
              className="textinput"
              placeholder={activeTab === 'organization' || activeTab === 'external' ? '이름 또는 소속으로 검색하세요.' : '이름 또는 연락처로 검색하세요.'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
            {inputValue && (
              <button type="button" className="input_cancelbtn" onClick={handleCancelSearch}>
                <span className="blind">취소</span>
              </button>
            )}
            <button type="button" className="input_searchbtn" onClick={handleSearch}>
              <span className="blind">검색</span>
            </button>
          </div>
        </div>
      </div>

      <div className="typeOrgList__tabBox">
        {([
          { key: 'organization' as const, label: '조직도' },
          { key: 'contact' as const, label: '연락처' },
          { key: 'external' as const, label: '외부참여자' },
        ]).map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`tabItem ${activeTab === tab.key ? 'selected' : ''}`}
            onClick={() => { 
               setActiveTab(tab.key); 
               setInputValue(''); 
               setSearchQuery({ text: '', version: 0 }); 
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="rtcOrganization">
        {activeTab === 'organization' && (
          <OrganizationTab
            query={searchQuery}
            selectedUsers={selectedUsers}
            onToggleUser={toggleUser}
            onRemoveUserFromOrg={removeUserFromOrg}
            cno={cno}
            companyNo={String(companyNo) || ''}
            myUserNo={local.userNo ? Number(local.userNo) : undefined}
            connectedPortalIds={connectedWehagoIds}
          />
        )}
        {activeTab === 'contact' && (
          <ContactTab 
            query={searchQuery} 
            selectedUsers={selectedUsers} 
            onToggleUser={toggleUser} 
            cno={cno} 
            companyNo={String(companyNo) || ''} 
          />
        )}
        {activeTab === 'external' && (
          <ExternalTab 
            query={searchQuery}
            selectedUsers={selectedUsers} 
            onToggleUser={toggleUser} 
            onCopyLink={handleCopyLink} 
          />
        )}

        {selectedUsers.length > 0 && (
          <SelectedUserBox selectedUsers={selectedUsers} onRemove={removeUser} />
        )}

        <div className="rtcOrganization__bottom">
          <button type="button" className="rtcOrganization__bottomBtn" onClick={handleBack}>
            취소
          </button>
          <button 
            type="button" 
            className={`rtcOrganization__bottomBtn blue ${selectedUsers.length === 0 ? 'disabled' : ''}`}
            onClick={handleSubmit}
            disabled={selectedUsers.length === 0 || isSubmitting}
          >
            초대
          </button>
        </div>
      </div>

      <SNSnackbar
        open={snackbar.open}
        type={snackbar.type || undefined}
        message={snackbar.message}
        autoHideDuration={4000}
        onRequestClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default InviteUser;
